import { GridColDef, GridColumnVisibilityModel, GridValueOptionsParams } from "@mui/x-data-grid-pro";
import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router";
import { Box, Tooltip, Grid, IconButton, Link, InputLabel, styled } from "@mui/material";
import moment from "moment";
import { DateFilterOperator, NumberFilterOperator, TaskProblemFilterOperator, TaskProgressFilterOperator, TaskStatusIdFilterOperator, TextFilterOperator } from "components/shared/filters";
import { TaskProgressPill } from "components/tasks";
import { ITask, TaskFilter } from "models/tasks";
import { formatDate } from "utils/date-utils";
import TaskViewProblem from "../TaskViewProblem";
import useGridColumns, { ColumnVisibilityOrOrderChangeHandler } from "hooks/shared/useGridColumns";
import { IDashboardSettings } from "components/preferences";
import { SetPreferenceSignature } from "hooks/preferences";
import { ListTooltip } from "components/shared";
import ContainsOnlyTextFilterOperator from "components/shared/filters/ContainsOnlyTextFilterOperator";
import { Ico_Bubble, Ico_Edit } from "@sagecentral/core-ui";
import { useTaskPermissions, useTaskStatusOptions } from "hooks/tasks";
import ParentWorkItemListTooltip from "components/teams/grid/ParentWorkItemListTooltip";
import { useFeatureEnabled } from "hooks/shared";
import { Link as RouterLink } from 'react-router-dom';

type UseTaskColumnsProps = {
  status: TaskFilter,
  hasViewProblemLogPrivileges: boolean,
  privilegesLoading: boolean,
  settings?: IDashboardSettings | null,
  setSettings?: SetPreferenceSignature,
};

type UseTaskColumnsResult = [
  GridColDef[],
  GridColumnVisibilityModel,
  ColumnVisibilityOrOrderChangeHandler,
];

type UseTaskColumnsSignature = (props: UseTaskColumnsProps) => UseTaskColumnsResult;

const useTaskColumns: UseTaskColumnsSignature = (props) => {
  const { status, hasViewProblemLogPrivileges, privilegesLoading } = props;
  const { settings, setSettings } = props;

  const permissions = useTaskPermissions();
  const [ wave4Enabled ] = useFeatureEnabled('wave-4');
  const { getOptionsForStatus } = useTaskStatusOptions();

  const StyledInputLabel = styled(InputLabel)(({ theme }) => ({
    padding: '0 16px',
    fontWeight: 600,
    color: '#333333',
    fontSize: '14px'
  }));

  const getStatusOptions = (params: GridValueOptionsParams<ITask>) => {
    if (params.row === undefined) return [];

    const task = params.row;
    const options = getOptionsForStatus(task.statusId, task.isMandatory, true);

    return options.map(o => ({ value: o.id, label: o.name }));
  };

  const columnBase: GridColDef[] = [
    {
      field: 'workItemId', headerName: 'WI ID', 
      width: 150,  sortable: true, resizable: false, hideable: false, hideSortIcons: false,
      filterOperators: NumberFilterOperator,
      renderCell: (params) => {
        return (
          <div aria-label={`Work Item ID: ${params.value}`} tabIndex={params.tabIndex}>
            {params.value}
          </div>
        )
      },
      renderHeader: (params) => {
        return (
          <Tooltip title="Work Item ID" arrow>
            <span aria-label={'Work Item ID'}>WI ID</span>
          </Tooltip>
        )
      }
    },
    {
      field: 'name', headerName: 'Task', flex: 1, minWidth: 300, sortable: true, resizable: true, hideable: false, headerClassName: 'header', hideSortIcons: false,
      filterOperators: TextFilterOperator,
      renderCell: (params) => {
        const name = params.value;
        const taskId = params.row.id;
        const unreadNotes = params.row.unreadNotes;
        const isTagged = params.row.isTagged;
        const hasAccess = params.row.userHasAccess;
        return (
          <Tooltip title={name} arrow onKeyDown={(e) => {
                    if (hasAccess && e.key === 'Enter') {
                        navigateToTask(taskId);
                    }
                }}>
            <Grid 
              container 
              alignItems="center" 
              style={{ whiteSpace: 'break-spaces', lineHeight: '1.5em', height: '3em' }}               
              aria-label={`Task: ${params.value}`}
              tabIndex={params.tabIndex}>
              <Grid item xs={10}>
                {hasAccess &&
                  <Link component={RouterLink} to={`/tasks/${taskId}`} aria-hidden='true'>
                    {name}
                  </Link>
                  || name}
              </Grid>
              <Grid item xs={2} style={{ textAlign: 'right' }}>
                {unreadNotes && unreadNotes > 0 &&
                  <span>
                    <Ico_Bubble style={{ height: '16px' }} />{unreadNotes}
                    {isTagged && isTagged == true && <span style={{ fontWeight: '400', textDecorationThickness: '2px' }}>@</span>}
                  </span>
                }
              </Grid>
            </Grid>
          </Tooltip>
        );
      }
    },
    {
      field: 'workItemName', headerName: 'Work Item', minWidth: 120, width: 200, sortable: true, resizable: true, hideable: false, headerClassName: 'header', hideSortIcons: false,
      filterOperators: TextFilterOperator,
      renderCell: (params) => {
        const name = params.value;
        const workItemId = params.row.workItemId;
        return (
          <Tooltip title={name} arrow onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                        navigate(`/workitems/${workItemId}/overview`, { state: { from: location }});
                      }
                    }
                  }
                >
            <div style={{ whiteSpace: 'break-spaces' }} 
              
              aria-label={`Work item: ${params.value}`}
              tabIndex={params.tabIndex}>
              <Link component={RouterLink} aria-hidden='true'
                to={wave4Enabled 
                  ? process.env.REACT_APP_PUBLIC_URL + `/workitems/${workItemId}/overview`
                  : process.env.REACT_APP_CLASSIC_URL + `/WorkitemEnduser/EditWorkitem?workitemID=${workItemId}`}>
                {name}
              </Link>
            </div>
          </Tooltip>
        );
      }
    },
    {
      field: 'contentType', headerName: 'Content Type', minWidth: 120, width: 200, sortable: true, resizable: true, hideable: true, headerClassName: 'header', hideSortIcons: false,
      filterOperators: TextFilterOperator,
      renderCell: (params) => {
        return (
          <div aria-label={`Content type: ${params.value}`} tabIndex={params.tabIndex}>
            {params.value}
          </div>
        )
      }
    },
    {
      field: 'parentWorkItems', headerName: 'Parent Work Item', minWidth: 120, width: 200, sortable: true, resizable: true, hideable: true, headerClassName: 'header', hideSortIcons: false,
      filterOperators: ContainsOnlyTextFilterOperator,
      renderCell: (params) => {
        const parents = params.value;
        return (
          <div aria-label={`Parent work item: ${params.value}`} tabIndex={params.tabIndex}>
            {
              parents?.length > 0 && <ParentWorkItemListTooltip canAccessAllWorkItems={permissions?.canAccessAllWorkItems} parent={parents ? parents : ''} />
            }
          </div>
        );
      }
    },
    {
      field: 'startDate', headerName: 'Start Date', minWidth: 120, width: 200, sortable: true, resizable: true, hideable: false, headerClassName: 'header', hideSortIcons: false, type: 'date',
      valueGetter: (value) => {
        return !value ? null : moment.utc(value).toDate();
      },
      filterOperators: DateFilterOperator,
      renderCell: (params) => {
        const startDate = params.value;
        return (
          <div aria-label={`Start date: ${formatDate(startDate)}`} tabIndex={params.tabIndex}>
            {formatDate(startDate)}
          </div>
        );
      }
    },
    {
      field: 'endDate', headerName: 'End Date', minWidth: 120, width: 200, sortable: true, resizable: true, hideable: false, headerClassName: 'header', hideSortIcons: false, type: 'date',
      valueGetter: (value) => {
        return !value ? null : moment(value).utc().toDate();
      },
      filterOperators: DateFilterOperator,
      editable: true,
      renderCell: (params) => {
        const endDate = params.row.endDate;
        return (
          <div aria-label={`End date: ${formatDate(endDate)}`} tabIndex={params.tabIndex}>
            {formatDate(endDate)}
            {params.row.statusId !== 6 && params.row.statusId !== 7 && !params.row.endDateLocked &&
              <IconButton
                aria-label='Edit End Date'
                onClick={(e) => {
                  e.preventDefault();
                  const clickEvent = new MouseEvent('dblclick', { 'bubbles': true });
                  e.target.dispatchEvent(clickEvent);
                }}
              >
                <Ico_Edit color='primary'
                  sx={{
                    top: '0',
                    mt: '-6px',
                    height: '20px',
                    width: '20px'
                  }}
                />
              </IconButton>
            }
          </div>
        );
      }
    },
    {
      field: 'statusId', headerName: 'Status', minWidth: 120, width: 200, sortable: true, resizable: true, hideable: true, 
      headerClassName: 'header',
      hideSortIcons: false,
      filterOperators: TaskStatusIdFilterOperator,
      editable: permissions?.canChangeStatus ?? false,
      type: 'singleSelect',
      valueOptions: getStatusOptions,
      renderCell: (params) => {
        return (
          <div aria-label={`Status: ${params.row.status}`} tabIndex={params.tabIndex}>
            {params.row.status}
            <IconButton
              aria-label='Edit Status'
              onClick={(e) => {
                e.preventDefault();
                  const clickEvent = new MouseEvent('dblclick', { 'bubbles': true});
                e.target.dispatchEvent(clickEvent);
              }}
            >
              <Ico_Edit color='primary'
                sx={{
                  top: '0',
                  mt: '-6px',
                  height: '20px',
                  width: '20px'
                }}
              />
            </IconButton>
          </div>
        );
      },
      renderHeader: (params) => {
        return (
          <StyledInputLabel htmlFor="status-select" id="status-select-label">Status</StyledInputLabel>
        )
      }
    },
    {
      field: 'progress', headerName: 'Progress', minWidth: 120, width: 200, sortable: true, resizable: true, hideable: false, headerClassName: 'header', hideSortIcons: false,
      filterOperators: TaskProgressFilterOperator(false),
      renderCell: (params) => {
        const taskStatus = params.row.status;
        return (
          <div aria-label={`Progress: ${params.value ?? ''}`} tabIndex={params.tabIndex}>
            {taskStatus?.toLowerCase() === 'in progress' && <TaskProgressPill progress={params.value} />}
          </div>
        )
      },
      renderHeader: (params) => {
        return (
          <StyledInputLabel htmlFor="progress-select" id="progress-select-label">Progress</StyledInputLabel>
        )
      }
    },
    {
      field: 'problem', headerName: 'Problem', headerClassName: 'header', minWidth: 120, width: 200, sortable: true, resizable: true, hideable: false, hideSortIcons: false,
      filterOperators: TaskProblemFilterOperator,
      renderCell: (params) => {
        const problem = params.row.problem;
        const taskId = params.row.id;
         return (
           <div aria-label={`Problem: ${params.value ?? ''}`} tabIndex={params.tabIndex}>
             {problem && <TaskViewProblem TaskId={taskId} />}
           </div>
         );
      },
      renderHeader: (params) => {
        return (
          <StyledInputLabel htmlFor="problem-select" id="problem-select-label">Problem</StyledInputLabel>
        )
      }
    },
    {
      field: 'workflowName', headerName: 'Workflow', minWidth: 120, width: 200, sortable: true, resizable: true, hideable: true, headerClassName: 'header', hideSortIcons: false,
      filterOperators: TextFilterOperator,
      renderCell: (params) => {
        const name = params.value;
        const workItemId = params.row.workItemId;
        return (
          <Tooltip title={name} arrow onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                        navigate(`/workitems/${workItemId}/workflows`, { state: { from: location }});
                      }
                    }
                  }>
            <div style={{ whiteSpace: 'break-spaces' }} aria-label={`Workflow: ${params.value}`} tabIndex={params.tabIndex}>
              {permissions?.canEditWorkflows ?
                <Link component={RouterLink} aria-hidden='true'
                  to={`/workitems/${workItemId}/workflows`}
                >
                  {name}
                </Link>
                : <>{name}</>
              }
            </div>
          </Tooltip>
        );
      }
    },
    {
      field: 'workItemTemplate', headerName: 'Work Item Template', minWidth: 120, width: 200, sortable: true, resizable: true, hideable: true, headerClassName: 'header', hideSortIcons: false,
      filterOperators: TextFilterOperator,
      renderCell: (params) => {
        return (
          <div aria-label={`Work Item Template: ${params.value}`} tabIndex={params.tabIndex}>
            {params.value}
          </div>
        )
      }
    },
  ];

  const navigate = useNavigate();
  const location = useLocation();

  const [columns, setColumns] = useState<GridColDef[]>(columnBase);

  const navigateToTask = (taskId: number) => {
    navigate(`/tasks/${taskId}`, { state: { from: location }});
  };

  const [columnsModel, visibilityModel, handleColumnVisibilityOrOrderChange] = useGridColumns({ columns: columns, settings, setSettings })

  useEffect(() => {
    let newColumns: GridColDef[] = [];
    newColumns.push(...columnBase);

    if (status !== 'all' && status !== 'in-progress') {
      const progressIdx = newColumns.findIndex((c) => c.field === 'progress');
      if (progressIdx > -1)
        newColumns.splice(progressIdx, 1);
    }

    if (!privilegesLoading && !hasViewProblemLogPrivileges) {
      const problemIdx = newColumns.findIndex((c) => c.field === 'problem');
      if (problemIdx > -1)
        newColumns.splice(problemIdx, 1);
    }
    setColumns(newColumns);
  }, [status, hasViewProblemLogPrivileges, privilegesLoading]);

  return [
    columnsModel,
    visibilityModel,
    handleColumnVisibilityOrOrderChange
  ];
};

export default useTaskColumns;